import axios, { AxiosResponse } from 'axios';
import { generateHeaders } from '../../utils/APIUtils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Reject } from '../Reject';
import { RootState } from '../index';
import { TokenData } from './auth';
import { Profile } from '../../domain';
import SocialNetwork from '../../domain/SocialNetwork';
import { FileUpload } from './file';
import UserSocialNetwork from '../../domain/UserSocialNetwork';
import { handleApiError } from '../handleApiError';
import { decodeToken } from '../../utils/decodeToken';

interface PasswordResetState {
    requestingPasswordReset: boolean;
    requestPasswordResetSuccess: boolean;
    requestPasswordResetError: boolean;

    resettingPassword: boolean;
    resetPasswordSuccess: boolean;
    resetPasswordError: boolean;
    resetPasswordErrorMsg?: string;

    changingPassword: boolean;
    changePasswordSuccess: boolean;
    changePasswordError: boolean;
    changePasswordErrorMsg?: string;
}

interface UserState extends PasswordResetState {
    creatingAccount: boolean;
    creatingAccountError: boolean;
    creatingAccountSuccess: boolean;
    createAccountData?: CreateAccountDto;
    error?: string;

    validatingEmail: boolean;
    validatingEmailError: boolean;
    validatingEmailSuccess: boolean;
    validatingEmailData?: object;
    validatingEmailErrorCont?: string;

    savingPassword: boolean;
    savingPasswordError: boolean;
    savingPasswordSuccess: boolean;
    savingPasswordData?: TokenDataResponse;
    savingPasswordErrorCont?: string;

    gettingProfile: boolean;
    gettingProfileSuccess: boolean;
    gettingProfileError: boolean;
    gettingProfileData: Profile;

    updatingProfile: boolean;
    updatingProfileSuccess: boolean;
    updatingProfileError: boolean;
}

const initialState: UserState = {
    creatingAccount: false,
    creatingAccountError: false,
    creatingAccountSuccess: false,
    createAccountData: undefined,
    error: undefined,

    validatingEmail: false,
    validatingEmailError: false,
    validatingEmailSuccess: false,
    validatingEmailData: undefined,

    savingPassword: false,
    savingPasswordError: false,
    savingPasswordSuccess: false,
    savingPasswordData: undefined,

    gettingProfile: false,
    gettingProfileSuccess: false,
    gettingProfileError: false,
    gettingProfileData: undefined,

    updatingProfile: false,
    updatingProfileSuccess: false,
    updatingProfileError: false,

    requestingPasswordReset: false,
    requestPasswordResetSuccess: false,
    requestPasswordResetError: false,

    resettingPassword: false,
    resetPasswordSuccess: false,
    resetPasswordError: false,

    changingPassword: false,
    changePasswordSuccess: false,
    changePasswordError: false,
};

interface CreateAccountDto {
    success: boolean;
    resent?: boolean;
}

interface CreateAccountResponse {
    data: CreateAccountDto;
    resent?: boolean;
}

interface CreateAccountParams {
    firstname?: string;
    lastname?: string;
    email: string;
    password?: string;
    terms: boolean;
    notifications?: boolean;
    isSimple?: boolean;
    captchaToken?: string;
}

export const createAccount = createAsyncThunk<CreateAccountResponse, CreateAccountParams, Reject>(
    'createAccount',
    async (params, thunkAPI) => {
        try {
            const { firstname, lastname, email, password, terms, notifications, captchaToken } = params;
            const isSimple = typeof params.isSimple !== 'undefined' ? params.isSimple : false;
            const { map } = thunkAPI.getState() as RootState;
            const { center, zoom } = map;

            const response = await axios.post<CreateAccountResponse>(
                `${process.env.REACT_APP_API_URL}/user`,
                {
                    isSimple,
                    firstname,
                    lastname,
                    email,
                    password,
                    terms,
                    notifications,
                    longitude: center.lat,
                    latitude: center.lng,
                    zoom,
                    captchaToken
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            return response.data;
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

export interface TokenDataResponse {
    jwt: string;
    tokenData: TokenData;
}

interface ValidateEmailParams {
    hash: string;
}

interface ApiResponse {
    jwt: string;
}

export const validateEmail = createAsyncThunk<TokenDataResponse, ValidateEmailParams, Reject>(
    'validateEmail',
    async (params, thunkAPI) => {
        try {
            const { hash } = params;
            const response = await axios.post<ApiResponse>(`${process.env.REACT_APP_API_URL}/user/confirmEmail`, {
                confirmationString: hash,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            try {
                const tokenData = decodeToken(response.data.jwt);
                return {
                    ...response.data,
                    tokenData,
                };
            } catch (e) {
                console.error('JWT Decode Error: ', e);
                throw new Error('Failed to decode JWT');
            }
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

interface SaveFirstPasswordParams {
    password: string;
}

export const saveFirstPassword = createAsyncThunk<TokenDataResponse, SaveFirstPasswordParams, Reject>(
    'saveFirstPassword',
    async (params, thunkAPI) => {
        try {
            const { password } = params;
            const { auth } = thunkAPI.getState() as RootState;
            const headers = generateHeaders(auth.jwt);

            // Explicitly type axios.put
            const response = await axios.put<ApiResponse>(
                `${process.env.REACT_APP_API_URL}/user/firstPassword`,
                { password },
                { headers }
            );

            // Ensure safe access and decoding of the JWT token
            const tokenData = decodeToken(response.data.jwt);
            return {
                ...response.data,
                tokenData,
            };
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

export const getProfile = createAsyncThunk<Profile, void, Reject>(
    'getProfile',
    async (_, thunkAPI) => {
        try {
            const { auth } = thunkAPI.getState() as RootState;
            const headers = generateHeaders(auth.jwt);

            const response: AxiosResponse<Profile> = await axios.get(
                `${process.env.REACT_APP_API_URL}/user/profile`,
                { headers }
            );
            if (!response.data) {
                throw new Error('Empty response');
            }
            return response.data;
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

interface ProfilePicture {
    idFile: string;
    url: string;
}

interface ProfileSocialNetwork {
    id: number;
    idSocialNetwork: number;
    name: string;
    shortName: string | null;
    profileLink: string | null;
    order: number | null;
    handle: string;
}

interface SaveProfileResponse {
    id: number;
    email: string;
    firstname: string;
    lastname: string;
    terms: number;
    notifications: number;
    emailVerified: number;
    latitude: string;
    longitude: string;
    zoom: string;
    socialNetworks: ProfileSocialNetwork[];
    picture: ProfilePicture;
    jwt: string;
}

interface SaveProfileParams {
    firstname: string;
    lastname: string;
    filesUploaded: FileUpload[];
    customSocialNetworks: UserSocialNetwork[];
    availableSocialNetworks: SocialNetwork[];
    networksToDelete: UserSocialNetwork[];
}

export const saveProfile = createAsyncThunk<SaveProfileResponse, SaveProfileParams, Reject>(
    'saveProfile',
    async (params, thunkAPI) => {
        try {
            const {
                firstname,
                lastname,
                filesUploaded,
                networksToDelete,
                customSocialNetworks,
                availableSocialNetworks
            } = params;

            const lastUploaded = filesUploaded?.length ? filesUploaded[filesUploaded.length - 1] : null

            const { auth } = thunkAPI.getState() as RootState;
            const headers = generateHeaders(auth.jwt);

            const response: AxiosResponse<SaveProfileResponse> = await axios.put(
                `${process.env.REACT_APP_API_URL}/user/profile`,
                {
                    firstname,
                    lastname,
                    idProfilePicture: lastUploaded?.idFile ?? undefined,
                    networksToDelete,
                    customSocialNetworks,
                    availableSocialNetworks
                },
                { headers }
            );

            return response.data;
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);


export const requestPasswordReset = createAsyncThunk<void, string, Reject>(
    'requestPasswordReset',
    async (email, thunkAPI) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/requestPasswordReset`,
                { email },
                { headers: { 'Content-Type': 'application/json' } }
            );
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

export const resetPassword = createAsyncThunk<TokenDataResponse, { hash: string; password: string }, Reject>(
    'resetPassword',
    async (params, thunkAPI) => {
        try {
            const response = await axios.put<ApiResponse>(
                `${process.env.REACT_APP_API_URL}/user/resetPassword`,
                params,
                { headers: { 'Content-Type': 'application/json' } }
            );
            const tokenData = decodeToken(response.data.jwt);
            return { ...response.data, tokenData };
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

export const changePassword = createAsyncThunk<TokenDataResponse, { currentPassword: string; newPassword: string }, Reject>(
    'changePassword',
    async (params, thunkAPI) => {
        try {
            const { auth } = thunkAPI.getState() as RootState;
            const headers = generateHeaders(auth.jwt);
            const response = await axios.put<ApiResponse>(
                `${process.env.REACT_APP_API_URL}/user/password`,
                params,
                { headers }
            );
            const tokenData = decodeToken(response.data.jwt);
            return { ...response.data, tokenData };
        } catch (error) {
            const apiError = handleApiError(error);
            return thunkAPI.rejectWithValue(apiError);
        }
    }
);

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        clearEditProfile: (state) => ({
            ...state,
            updatingProfile: initialState.updatingProfile,
            updatingProfileError: initialState.updatingProfileError,
            updatingProfileSuccess: initialState.updatingProfileSuccess,
            gettingProfile: initialState.gettingProfile,
            gettingProfileSuccess: initialState.gettingProfileSuccess,
            gettingProfileError: initialState.gettingProfileError,
            gettingProfileData: initialState.gettingProfileData,
        }),
        clearAccountCreation: (state) => {
            state.creatingAccount = false;
            state.creatingAccountError = false;
            state.creatingAccountSuccess = false;
            delete state.createAccountData;
        },
        clearEmailValidation: (state) => {
            state.creatingAccount = false;
            state.validatingEmail = false;
            state.validatingEmailError = false;
            state.validatingEmailSuccess = false;
            state.validatingEmailData = undefined;
            state.error = undefined;
        },
        getSample: (state) => {
            state.gettingProfile = false;
            state.gettingProfileSuccess = true;
            state.gettingProfileData = {
                'terms': true,
                'lastname': 'Carpentier',
                'zoom': '12',
                'longitude': '151.24270914417207',
                'firstname': 'Mariano',
                'latitude': '-33.816446995619465',
                'customSocialNetworks': undefined,
                'availableSocialNetworks': undefined,
                'spots': undefined,
                'picture': undefined,
                'socialNetworks': undefined,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(saveProfile.pending, (state) => {
                state.updatingProfile = true;
                state.updatingProfileError = false;
                state.updatingProfileSuccess = false;
            })
            .addCase(saveProfile.rejected, (state) => {
                state.updatingProfile = false;
                state.updatingProfileError = true;
                state.updatingProfileSuccess = false;
            })
            .addCase(saveProfile.fulfilled, (state) => {
                state.updatingProfile = false;
                state.updatingProfileError = false;
                state.updatingProfileSuccess = true;
            })
            .addCase(createAccount.pending, (state) => {
                state.creatingAccount = true;
                state.creatingAccountError = false;
                state.creatingAccountSuccess = false;
                delete state.createAccountData;
                delete state.error;
            })
            .addCase(createAccount.rejected, (state, action) => {
                state.creatingAccount = false;
                state.creatingAccountError = true;
                state.creatingAccountSuccess = false;
                delete state.createAccountData;
                state.error = action.payload?.message ?? 'Can\'t create the account right now.';
            })
            .addCase(createAccount.fulfilled, (state, action) => {
                state.creatingAccount = false;
                state.creatingAccountError = false;
                state.creatingAccountSuccess = true;
                if ((action.payload)?.resent) {
                    state.error = 'EMAIL_UNVERIFIED_RESENT';
                } else {
                    delete state.error;
                }
                state.createAccountData = action.payload?.data;
            })
            .addCase(validateEmail.pending, (state) => {
                state.validatingEmail = true;
                state.validatingEmailError = false;
                state.validatingEmailSuccess = false;
                delete state.validatingEmailData;
                delete state.error;
                delete state.validatingEmailErrorCont;
            })
            .addCase(validateEmail.rejected, (state, action) => {
                state.validatingEmail = false;
                state.validatingEmailError = true;
                state.validatingEmailErrorCont = action.payload?.message ?? 'Can\'t verify email right now.';
            })
            .addCase(validateEmail.fulfilled, (state, action) => {
                state.validatingEmail = false;
                state.validatingEmailSuccess = true;
                state.validatingEmailData = action.payload;
            })
            .addCase(saveFirstPassword.pending, (state) => {
                state.savingPassword = true;
                state.savingPasswordError = false;
                state.savingPasswordSuccess = false;
                delete state.savingPasswordData;
                delete state.savingPasswordErrorCont;
            })
            .addCase(saveFirstPassword.rejected, (state, action) => {
                state.savingPassword = false;
                state.savingPasswordError = true;
                state.savingPasswordErrorCont = action.payload?.message ?? 'Can\'t verify email right now.';
            })
            .addCase(saveFirstPassword.fulfilled, (state, action) => {
                state.savingPassword = false;
                state.savingPasswordSuccess = true;
                state.savingPasswordData = action.payload;
            })

            .addCase(getProfile.pending, (state) => {
                state.gettingProfile = true;
                state.gettingProfileSuccess = false;
                state.gettingProfileError = false;
                delete state.gettingProfileData;
            })
            .addCase(getProfile.rejected, (state) => {
                state.gettingProfile = false;
                state.gettingProfileError = true;
            })
            .addCase(getProfile.fulfilled, (state, action) => {
                state.gettingProfile = false;
                state.gettingProfileSuccess = true;
                state.gettingProfileData = action.payload;
            })
            .addCase(requestPasswordReset.pending, (state) => {
                state.requestingPasswordReset = true;
                state.requestPasswordResetSuccess = false;
                state.requestPasswordResetError = false;
            })
            .addCase(requestPasswordReset.rejected, (state) => {
                state.requestingPasswordReset = false;
                state.requestPasswordResetError = true;
            })
            .addCase(requestPasswordReset.fulfilled, (state) => {
                state.requestingPasswordReset = false;
                state.requestPasswordResetSuccess = true;
            })
            .addCase(resetPassword.pending, (state) => {
                state.resettingPassword = true;
                state.resetPasswordSuccess = false;
                state.resetPasswordError = false;
                delete state.resetPasswordErrorMsg;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.resettingPassword = false;
                state.resetPasswordError = true;
                state.resetPasswordErrorMsg = action.payload?.message;
            })
            .addCase(resetPassword.fulfilled, (state) => {
                state.resettingPassword = false;
                state.resetPasswordSuccess = true;
            })
            .addCase(changePassword.pending, (state) => {
                state.changingPassword = true;
                state.changePasswordSuccess = false;
                state.changePasswordError = false;
                delete state.changePasswordErrorMsg;
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.changingPassword = false;
                state.changePasswordError = true;
                state.changePasswordErrorMsg = action.payload?.message;
            })
            .addCase(changePassword.fulfilled, (state) => {
                state.changingPassword = false;
                state.changePasswordSuccess = true;
            });
    },
});

export const {
    clearAccountCreation,
    clearEmailValidation,
    clearEditProfile,
    getSample
} = userSlice.actions;

export default userSlice.reducer;
